import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import Reveal from "../components/Reveal";
import SEO from "../components/SEO";
// import ComposerAllure from "../components/ComposerAllure"; // réactiver avec les vraies photos
import { HERO_IMAGE, MONDES, PRODUCTS, formatPrice } from "../lib/pulaar";

function Hero() {
  const { t } = useTranslation();
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-end overflow-hidden"
    >
      <motion.div
        initial={{ scale: 1.1 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
        className="absolute inset-0"
      >
        <img
          src={HERO_IMAGE}
          alt="Pulaar — Héritage en mouvement"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#FBF8EF]/40 via-transparent to-black/55" />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-[1600px] w-full px-6 md:px-12 lg:px-16 pb-16 md:pb-24">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4 }}
          className="text-[11px] tracking-[0.32em] uppercase text-[#FBF8EF]/85 text-right"
        >
          {t("home.heroEyebrow")}
        </motion.p>
      </div>
    </section>
  );
}

function TroisMondes() {
  const { t } = useTranslation();
  return (
    <section
      data-testid="home-mondes"
      className="py-24 md:py-32 bg-[#FBF8EF]"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-16">
        <Reveal>
          <h1
            data-testid="home-heritage-title"
            className="font-serif text-[44px] sm:text-[64px] md:text-[88px] lg:text-[112px] leading-[0.95] tracking-[-0.02em] text-[#121212] text-right mb-20 md:mb-28"
          >
            {t("home.heritage")} <span className="italic font-light">{t("home.heritageEm")}</span>
          </h1>
        </Reveal>
        <Reveal>
          <p className="text-[11px] tracking-[0.32em] uppercase text-[#5C4033] mb-6">
            {t("home.troisEyebrow")}
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif text-4xl md:text-6xl tracking-[-0.02em] leading-[1.05] text-[#121212] max-w-[18ch]">
            {t("home.troisTitleA")}<span className="italic font-light">{t("home.troisTitleEm")}</span>{t("home.troisTitleC")}
          </h2>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-4 max-w-xl text-base text-[#4A4641]">
            {t("home.troisDesc")}
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
          {MONDES.map((m, i) => (
            <Reveal key={m.slug} delay={i * 0.1}>
              <Link
                to={`/archives/${m.slug}`}
                data-testid={`home-monde-${m.slug}`}
                className="group block"
              >
                <div className="relative aspect-[3/4] overflow-hidden bg-[#F3EFE7]">
                  <img
                    src={m.cover}
                    alt={m.name}
                    className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
                  />
                  <span className="absolute top-4 left-4 font-serif italic text-2xl text-[#FBF8EF] mix-blend-difference">
                    {m.symbol}
                  </span>
                </div>
                <div className="mt-5 flex items-baseline justify-between">
                  <h3 className="font-serif text-2xl md:text-3xl text-[#121212]">
                    {m.name}
                  </h3>
                  <ArrowRight size={16} strokeWidth={1.2} className="text-[#5C4033]" />
                </div>
                <p className="mt-1 font-serif italic text-sm text-[#5C4033]">
                  {t(`mondes.${m.slug}.subtitle`, { defaultValue: m.subtitle })}
                </p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function PiecesACommander() {
  const { t } = useTranslation();
  const featured = PRODUCTS.slice(0, 4);
  return (
    <section
      data-testid="home-pieces"
      className="py-24 md:py-32 bg-[#F3EFE7]"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <Reveal>
            <p className="text-[11px] tracking-[0.32em] uppercase text-[#5C4033] mb-4">
              {t("home.vestiaireEyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-6xl tracking-[-0.02em] leading-[1.05] text-[#121212]">
              {t("home.vestiaireTitleA")}<span className="italic font-light">{t("home.vestiaireTitleEm")}</span>{t("home.vestiaireTitleC")}
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/vestiaire"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-[#121212] group"
              data-testid="home-pieces-all-link"
            >
              <span className="link-underline">{t("home.seeAll")}</span>
              <ArrowRight
                size={14}
                strokeWidth={1.2}
                className="transition-transform duration-500 group-hover:translate-x-1"
              />
            </Link>
          </Reveal>
        </div>

        {/* Composer une allure — masqué temporairement (en attente des vraies photos)
        <Reveal>
          <ComposerAllure />
        </Reveal>
        */}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-12">
          {featured.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 4) * 0.08}>
              <Link
                to={`/vestiaire/${p.slug}`}
                data-testid={`home-product-${p.slug}`}
                className="group block"
              >
                <div className="relative overflow-hidden aspect-[3/4] bg-[#FBF8EF]">
                  <img
                    src={p.image}
                    alt={p.name}
                    draggable="false"
                    onContextMenu={(e) => e.preventDefault()}
                    className="w-full h-full object-cover transition-all duration-[1400ms] ease-out group-hover:scale-[1.02] blur-[18px] pointer-events-none select-none"
                  />
                  <span className="absolute top-3 left-3 px-2.5 py-1 bg-[#FBF8EF]/90 backdrop-blur-sm text-[10px] tracking-[0.32em] uppercase text-[#5C4033]">
                    {t(`product.status${p.status.charAt(0).toUpperCase() + p.status.slice(1).replace("_o", "O")}`, { defaultValue: p.status })}
                  </span>
                </div>
                <div className="mt-4 flex items-baseline justify-between gap-3 blur-[14px] opacity-70 select-none">
                  <h3 className="font-serif text-lg md:text-xl text-[#121212] leading-tight">
                    {p.name}
                  </h3>
                  <span className="font-sans text-sm text-[#4A4641]">
                    {formatPrice(p.price)}
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function AtelierTeaser() {
  const { t } = useTranslation();
  return (
    <section
      data-testid="home-atelier"
      className="relative py-24 md:py-36 text-[#FBF8EF] overflow-hidden"
    >
      {/* Image en arrière-plan : cases peules */}
      <div className="absolute inset-0">
        <img
          src="/heritage/pulaar-cases-peules-architecture-vernaculaire.jpg"
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/55 to-black/65" />
      </div>

      <div className="relative mx-auto max-w-[1100px] px-6 md:px-12 text-center">
        <Reveal>
          <p className="text-[11px] tracking-[0.32em] uppercase text-[#E5DFD3] mb-8">
            {t("home.atelierEyebrow")}
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[1.15] tracking-[-0.01em] text-balance">
            {t("home.atelierTaglineA")}<span className="italic font-light">{t("home.atelierTaglineEm")}</span>{t("home.atelierTaglineC")}
          </p>
        </Reveal>
        <Reveal delay={0.25}>
          <Link
            to="/archives"
            data-testid="home-atelier-link"
            className="mt-12 inline-flex items-center gap-3 group text-[11px] tracking-[0.32em] uppercase"
          >
            <span className="link-underline">{t("home.exploreArchives")}</span>
            <ArrowUpRight
              size={14}
              strokeWidth={1.2}
              className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
            />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main data-testid="home-page" className="relative">
      <SEO
        title="Vestiaire éditorial peul"
        description="Pulaar — vestiaire éditorial. Pièces tissées main, atelier Paris, mémoire peule. Saison 01 bientôt en atelier."
        path="/"
      />
      <Hero />
      <TroisMondes />
      <PiecesACommander />
      <AtelierTeaser />
    </main>
  );
}
